import React, {useEffect, useRef, useState} from "react";
import DeviceCommandsList from './DeviceCommandsList';
import "./DeviceCard.css";
import {Accordion, ListGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle, faShield, faSquare, faUser, faLightbulb, faHouse, faCode} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Row from "react-bootstrap/Row";
import InputGroup from "react-bootstrap/InputGroup";
import {tagColor, deviceTypeBadgeColor, getBondColor} from "../../../../../../lib/ui/colorUtils";
import ReactJson from "react-json-view";
import { useTimezone } from "../../../../contexts/TimezoneContext";


/**
 * Lazy-loads device commands only when the user opens this accordion section.
 */
function CommandsPanel({ bondId, deviceId }) {
    const [loaded, setLoaded] = useState(false);

    return (
        <Accordion className="mt-2" onSelect={key => { if (key === '0') setLoaded(true); }}>
            <Accordion.Item eventKey="0">
                <Accordion.Header>Commands</Accordion.Header>
                <Accordion.Body className="p-2">
                    {loaded && <DeviceCommandsList bondId={bondId} deviceId={deviceId} />}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

function DeviceCard({ devices = [] }) {

    const [renderedDevices, setRenderedDevices] = useState({});
    const filterTerm = useRef();
    const { formatDate, getRelativeTime } = useTimezone();

    useEffect(() => {
        renderDevices();
    }, [devices, formatDate]);

    function filterDevice(device) {
        const term = filterTerm.current.value.toLowerCase();
        if (term === "") return false;
        const lowerTerm = term.toLowerCase();
        return !JSON.stringify(device).toLowerCase().includes(lowerTerm);
    }

    // Check if device belongs to a Smart by Bond (SBB) device
    function isSBBDevice(device) {
        if (!device.bond_id) return false;
        const firstChar = device.bond_id.charAt(0);
        return firstChar === 'T' || firstChar === 'K' || firstChar === 'H';
    }


    function renderDeviceProperties(device) {
        const infoKeys = ["state", "properties"];
        return (<Accordion className={"mt-2"}>
            {infoKeys.map((key, index) => device[key] ? (<Accordion.Item eventKey={index.toString()} key={key}>
                <Accordion.Header>{key.charAt(0).toUpperCase() + key.slice(1)}</Accordion.Header>
                <Accordion.Body className={"p-3"}>
                    {typeof device[key] === "object" && !Array.isArray(device[key]) ? (Object.entries(device[key]).sort(([a], [b]) => {
                        if (a.startsWith("_") && !b.startsWith("_")) return 1;
                        if (!a.startsWith("_") && b.startsWith("_")) return -1;
                        return a.localeCompare(b);
                    }).map(([subKey, subValue]) => (
                        <FloatingLabel label={subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                                       key={subKey}>
                            <Form.Control readOnly={true} value={subValue ?? ""}/>
                        </FloatingLabel>))) : (<FloatingLabel label={key.charAt(0).toUpperCase() + key.slice(1)}>
                        <Form.Control readOnly={true} value={device[key] ?? ""}/>
                    </FloatingLabel>)}
                </Accordion.Body>
            </Accordion.Item>) : null)}
        </Accordion>);
    }

    function renderDevices() {
        if (!devices || devices.length === 0) {
            setRenderedDevices({});
            return;
        }

        // Group devices by location
        const devicesByLocation = {};

        devices.forEach((device) => {
            if (filterDevice(device)) return;

            const location = device.location || "No Location";
            if (!devicesByLocation[location]) {
                devicesByLocation[location] = [];
            }
            devicesByLocation[location].push(device);
        });

        // Render devices grouped by location
        const _renderedDevices = {};

        Object.keys(devicesByLocation).sort().forEach((location) => {
            _renderedDevices[location] = devicesByLocation[location].map((device, index) => {
                return <Accordion.Item eventKey={`${device.device_id || index}-${device.bond_id || 'unknown'}`}
                                       key={`${device.device_id || index}-${device.bond_id || 'unknown'}`}>
                    <Accordion.Header>
                        <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                            <Badge className="flex-shrink-0" style={deviceTypeBadgeColor(device.device_type || device.type)}>
                                {device.device_type || device.type || "Device"}
                            </Badge>
                            <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                {device.name}
                            </span>
                            <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                {(device.properties?.branding_profile || device.properties?.pairing_assets_key) && (
                                    <Badge bg={"dark"}>
                                        {device.properties.branding_profile || device.properties.pairing_assets_key}
                                    </Badge>
                                )}
                                {device.bond_id && (
                                    <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(device.bond_id)}}>{device.bond_id}
                                    </Badge>
                                )}
                                {device.template_id ? (
                                    <Badge bg={"info"}
                                           style={tagColor("template", device.template_id)}>{device.template_id}
                                    </Badge>
                                ) : device.template ? (
                                    <Badge bg={"info"}
                                           style={tagColor("template", device.template)}>{device.template}
                                    </Badge>
                                ) : (
                                    <Badge bg={"warning"}>
                                        {isSBBDevice(device) ? 'SBB' : 'RAW'}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className={"p-3"}>
                        <div className="d-flex gap-2 mb-2">
                            <FloatingLabel label="Name" className="flex-fill">
                                <Form.Control readOnly={true} value={device.name || ""}/>
                            </FloatingLabel>
                            {device.location && (
                                <FloatingLabel label="Location" className="flex-fill">
                                    <Form.Control readOnly={true} value={device.location}/>
                                </FloatingLabel>
                            )}
                        </div>

                        {device.type && (
                            <FloatingLabel label="Type" className="mb-2">
                                <Form.Control readOnly={true} value={device.type}/>
                            </FloatingLabel>
                        )}

                        <div className="d-flex gap-2 mb-2">
                            {device.device_id && (
                                <FloatingLabel label="Device ID" className="flex-fill">
                                    <Form.Control readOnly={true} value={device.device_id}/>
                                </FloatingLabel>
                            )}
                            {device.template_id && (
                                <FloatingLabel label="Template ID" className="flex-fill">
                                    <Form.Control readOnly={true} value={device.template_id}/>
                                </FloatingLabel>
                            )}
                        </div>

                        <div className="d-flex gap-2 mb-2">
                            {device.created && (
                                <FloatingLabel label={`Created ${getRelativeTime(device.created)}`} className="flex-fill">
                                    <Form.Control readOnly={true} value={formatDate(device.created)}/>
                                </FloatingLabel>
                            )}
                            {device.modified && (
                                <FloatingLabel label={`Modified ${getRelativeTime(device.modified)}`} className="flex-fill">
                                    <Form.Control readOnly={true} value={formatDate(device.modified)}/>
                                </FloatingLabel>
                            )}
                        </div>

                        {/* Actions Section */}
                        {device.actions && Array.isArray(device.actions) && device.actions.length > 0 && (
                            <div className="mb-3">
                                <h6>Actions</h6>
                                <div className="d-flex flex-wrap gap-1">
                                    {[...device.actions].sort().map((action, idx) => (
                                        <Badge key={idx} bg="info">{action}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {renderDeviceProperties(device)}

                        {/* Commands Section — lazy-loaded on first expand */}
                        {device.device_id && device.bond_id && (
                            <CommandsPanel bondId={device.bond_id} deviceId={device.device_id} />
                        )}

                        {/* JSON Viewer Section */}
                        <div className="mt-3">
                            <Accordion>
                                <Accordion.Item eventKey="json">
                                    <Accordion.Header>
                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                        Raw JSON
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <ReactJson
                                            src={device}
                                            theme="ocean"
                                            collapsed={1}
                                            displayDataTypes={false}
                                            displayObjectSize={false}
                                            enableClipboard={true}
                                            name={false}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </div>
                    </Accordion.Body>

                </Accordion.Item>
            });
        });

        setRenderedDevices(_renderedDevices);
    }


    if (!devices || devices.length === 0) {
        return null;
    }

    const hasDevices = Object.keys(renderedDevices).length > 0;
    const totalDevices = devices.length;

    return (<Accordion defaultActiveKey="0" className="mb-2">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                                Devices
                            </div>
                            <Badge bg="dark">
                                {totalDevices}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-2">
                        <Row>
                            <InputGroup>
                                <Form.Control
                                    ref={filterTerm}
                                    placeholder="Filter devices..."
                                    onChange={renderDevices}
                                    className={"mb-2"}
                                />
                            </InputGroup>
                        </Row>
                        {hasDevices ? (
                            Object.keys(renderedDevices).sort().map((location) => (
                                <div key={location} className="mb-3">
                                    <h6 className="text-muted mb-2 ms-2">
                                        <FontAwesomeIcon icon={faHouse} className="me-2"/>
                                        {location}
                                        <Badge bg="info" className="ms-2 small">
                                            {renderedDevices[location].length}
                                        </Badge>
                                    </h6>
                                    <Accordion alwaysOpen>
                                        {renderedDevices[location]}
                                    </Accordion>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted p-3">No devices match the filter</div>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>);
}

export default DeviceCard;
